import { X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { MentionSuggestion, type MentionUser } from "./MentionSuggestion"

export interface CommentSubmitOptions {
  notifyMembers?: boolean
}

interface CommentFormProps {
  onSubmit: (content: string, options?: CommentSubmitOptions) => Promise<void> | void
  placeholder: string
  onCancel?: () => void
  initialValue?: string
  disabled?: boolean
  autoFocus?: boolean
  onSearchUsers?: (query: string) => Promise<MentionUser[]>
  onMentionSelect?: (user: MentionUser) => void
  /** Shows the "Also email all members" checkbox (ritual organizers, top-level only). */
  showNotifyMembers?: boolean
  notifyMembersLabel?: string
}

const MENTION_REGEX = /@(\w*)$/

export function CommentForm({
  onSubmit,
  placeholder,
  onCancel,
  initialValue = "",
  disabled = false,
  autoFocus = false,
  onSearchUsers,
  onMentionSelect,
  showNotifyMembers = false,
  notifyMembersLabel,
}: CommentFormProps) {
  const [value, setValue] = useState(initialValue)
  const [notifyMembers, setNotifyMembers] = useState(false)
  const [_mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [mentionUsers, setMentionUsers] = useState<MentionUser[]>([])
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [showMentionDropdown, setShowMentionDropdown] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const mentionStartPosRef = useRef<number>(0)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const handleSubmit = async () => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return

    await onSubmit(trimmed, showNotifyMembers ? { notifyMembers } : undefined)
    if (!initialValue) {
      setValue("")
      setNotifyMembers(false)
    }
  }

  const searchUsers = useCallback(
    async (query: string) => {
      if (!onSearchUsers || query.length < 1) {
        setMentionUsers([])
        return
      }

      setIsSearching(true)
      try {
        const users = await onSearchUsers(query)
        setMentionUsers(users)
        setMentionSelectedIndex(0)
      } catch {
        setMentionUsers([])
      } finally {
        setIsSearching(false)
      }
    },
    [onSearchUsers],
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    const cursorPos = e.target.selectionStart || 0
    setValue(newValue)

    const textBeforeCursor = newValue.slice(0, cursorPos)
    const match = textBeforeCursor.match(MENTION_REGEX)

    if (match) {
      const query = match[1]
      mentionStartPosRef.current = cursorPos - match[0].length
      setMentionQuery(query)
      setShowMentionDropdown(true)

      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      if (query.length >= 1) {
        debounceRef.current = setTimeout(() => {
          searchUsers(query)
        }, 300)
      }
    } else {
      setShowMentionDropdown(false)
      setMentionQuery(null)
      setMentionUsers([])
    }
  }

  const insertMention = useCallback(
    (user: MentionUser) => {
      const cursorPos = inputRef.current?.selectionStart || value.length
      const mentionText = `@${user.username} `
      const beforeMention = value.slice(0, mentionStartPosRef.current)
      const afterMention = value.slice(cursorPos)
      const newValue = beforeMention + mentionText + afterMention

      setValue(newValue)
      setShowMentionDropdown(false)
      setMentionQuery(null)
      setMentionUsers([])

      onMentionSelect?.(user)

      setTimeout(() => {
        const newCursorPos = mentionStartPosRef.current + mentionText.length
        inputRef.current?.setSelectionRange(newCursorPos, newCursorPos)
        inputRef.current?.focus()
      }, 0)
    },
    [value, onMentionSelect],
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showMentionDropdown || mentionUsers.length === 0) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
      return
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setMentionSelectedIndex(prev => (prev + 1) % mentionUsers.length)
        break
      case "ArrowUp":
        e.preventDefault()
        setMentionSelectedIndex(prev => (prev - 1 + mentionUsers.length) % mentionUsers.length)
        break
      case "Enter":
        e.preventDefault()
        insertMention(mentionUsers[mentionSelectedIndex])
        break
      case "Escape":
        e.preventDefault()
        setShowMentionDropdown(false)
        setMentionQuery(null)
        setMentionUsers([])
        break
      case "Tab":
        e.preventDefault()
        insertMention(mentionUsers[mentionSelectedIndex])
        break
    }
  }

  useEffect(() => {
    if (showMentionDropdown && mentionUsers.length > 0) {
      const handleClickOutside = () => {
        setShowMentionDropdown(false)
      }
      document.addEventListener("click", handleClickOutside)
      return () => document.removeEventListener("click", handleClickOutside)
    }
  }, [showMentionDropdown, mentionUsers.length])

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="h-16.25 py-5 px-3.75 rounded-[15px] border-[#F2F0EB26] bg-[#F2F0EB0D] text-sm pr-24"
          autoFocus={autoFocus}
        />
        <div className="absolute right-3.75 top-1/2 -translate-y-1/2 flex items-center gap-2">
          <Button
            variant="ghost"
            className="p-0 w-6.25 h-6.25"
            onClick={handleSubmit}
            disabled={disabled || !value.trim()}
            aria-label="Post comment"
          >
            <img src={`${import.meta.env.VITE_ASSET_BASE_URL}send.svg`} alt="Send" />
          </Button>
          {onCancel && (
            <Button variant="outline" size="sm" className="w-7 h-7" onClick={onCancel}>
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {showMentionDropdown && onSearchUsers && (
          <MentionSuggestion
            users={mentionUsers}
            selectedIndex={mentionSelectedIndex}
            isLoading={isSearching}
            onSelect={insertMention}
            onHover={setMentionSelectedIndex}
          />
        )}
      </div>

      {showNotifyMembers && (
        <label className="mt-5 flex items-center gap-2.5 cursor-pointer select-none">
          <Checkbox
            checked={notifyMembers}
            onCheckedChange={checked => setNotifyMembers(checked === true)}
            disabled={disabled}
            aria-label={notifyMembersLabel ?? "Also notify all participants"}
            className="w-[24px] h-[24px]"
          />
          <span className="text-[14px] text-primary/80">
            {notifyMembersLabel ?? "Also notify all participants"}
          </span>
        </label>
      )}
    </div>
  )
}
